import { VerifyAuthResponse } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import jwtDecode from 'jwt-decode';
import * as oauth from 'oauth4webapi';
import {
  AuthStorage,
  DecodedOIDCIDToken,
  isWeaveTokenActive,
  localStorageHelper,
  OidcClientConfig,
  SignInMethod,
  encodeAuthState,
  AUTH_STATE_VERSION,
  AuthState,
  decodeAuthState,
} from '@frontend/auth-helpers';
import { sentry } from '@frontend/tracking';
import { storeTimeToRedirectToAuth } from '../logging/time-to-redirect-logger';
import { navigateToUrl } from '../utils/navigateToUrl';
import { OIDC_SESSION_KEYS, oidcSessionStore } from '../utils/oidc-session-store';
import { AuthFlow, SignInProps, SignOutProps, ClientTokens, HandleCallbackProps } from './AuthFlow';

const s256 = 'S256';
const tokenEndpointAuthMethod = 'none';

// implementation based on the example given by oauth4webapi:
// https://github.com/panva/oauth4webapi/blob/main/examples/public.ts

export class OidcClient implements AuthFlow {
  private config: OidcClientConfig;
  private as: oauth.AuthorizationServer | undefined;
  private authState: AuthState | undefined;

  constructor(config: OidcClientConfig) {
    this.config = config;
  }

  /** for testing only */
  public resetInternalState() {
    this.as = undefined;
    this.authState = undefined;
  }

  public getAuthState(): AuthState | undefined {
    return this.authState;
  }

  public async signIn(props?: SignInProps): Promise<VerifyAuthResponse> {
    const clientId = props?.clientId || this.config.clientID;
    const redirectUri = props?.redirectUri || this.config.redirectUri;
    if (!clientId || !redirectUri) {
      throw new Error('oidc client not configured correctly, must provide both clientId and redirectUri');
    }

    const as = await this.getAuthenticationServer();
    // check if the server supports PKCE
    if (as.code_challenge_methods_supported?.includes(s256) !== true || !as.authorization_endpoint) {
      throw new Error('OIDC server does not support PKCE or authorization endpoint');
    }
    const code_verifier = oauth.generateRandomCodeVerifier();
    const nonce = oauth.generateRandomState();
    const code_challenge = await oauth.calculatePKCECodeChallenge(code_verifier);
    const code_challenge_method = s256;
    oidcSessionStore.setItem(OIDC_SESSION_KEYS.codeVerifier, code_verifier);
    oidcSessionStore.setItem(OIDC_SESSION_KEYS.stateNonce, nonce);

    const authorizationUrl = new URL(as.authorization_endpoint);
    authorizationUrl.searchParams.set('client_id', clientId);
    authorizationUrl.searchParams.set('code_challenge', code_challenge);
    authorizationUrl.searchParams.set('code_challenge_method', code_challenge_method);
    authorizationUrl.searchParams.set('redirect_uri', redirectUri.toString());
    authorizationUrl.searchParams.set('response_type', 'code');
    authorizationUrl.searchParams.set('scope', 'openid email');
    const state = encodeAuthState({ v: AUTH_STATE_VERSION, nonce, ...props?.state });
    if (state.length > 2048) {
      sentry.warn({
        error: new Error('Auth state got longer than 2048 characters'),
        topic: 'auth',
        team: 'users-infra',
      });
    }
    authorizationUrl.searchParams.set('state', state);

    // this perf log measures the time it takes to:
    // 1. go to NWX
    // 2. whole app loads
    // 3. token is expired so redirect to auth.getweave
    // this is the lastest possible moment to log that
    if (
      props?.meta?.source === 'auth_check' &&
      !window.location.origin.includes('auth') &&
      !this.isUserAuthenticated()
    ) {
      storeTimeToRedirectToAuth(props?.meta?.authRedirectExpGroup);
    }

    navigateToUrl(authorizationUrl.toString(), {
      reason: 'OIDC signIn authorization endpoint redirect',
      loopPolicy: props?.loopPolicy,
    });
    return Promise.resolve({});
  }

  // must verify it before consuming it because it is a security measure to prevent CSRF attacks
  private verifyAuthStateNonce(nonce: string | undefined): boolean {
    const storedNonce = oidcSessionStore.getItem<string | null>(OIDC_SESSION_KEYS.stateNonce, null);
    oidcSessionStore.removeItem(OIDC_SESSION_KEYS.stateNonce);
    return !!storedNonce && storedNonce === nonce;
  }

  private async getAuthenticationServer() {
    if (this.as) {
      return Promise.resolve(this.as);
    }
    const discoveryResponse = await oauth.discoveryRequest(this.config.issuer);
    this.as = await oauth.processDiscoveryResponse(this.config.issuer, discoveryResponse);
    return this.as;
  }

  public async signOut(props?: SignOutProps): Promise<void> {
    // Cache oidc token in memory and then clear. The OIDC token stores user specific data.
    const oidc_token = localStorageHelper.get<{ accessToken: string; IDToken: string }>(AuthStorage.oidc_token_storage);
    localStorageHelper.delete(AuthStorage.oidc_token_storage);

    const as = await this.getAuthenticationServer();

    if (!as.end_session_endpoint) {
      if (props?.logoutRedirect) {
        navigateToUrl(props?.logoutRedirect, {
          reason: 'OIDC signOut redirect, no end_session_endpoint, using logoutRedirect',
        });
      } else {
        sentry.error({
          error: 'oidc.signout - no end_session_endpoint, redirect to /',
          topic: 'auth',
          team: 'users-infra',
          sampleRate: 1,
        });
        navigateToUrl('/', { reason: 'OIDC signOut redirect, no end_session_endpoint, using /' });
      }
      return;
    }

    const redirectUri = `${window.location.origin}`;
    const params = new URLSearchParams();

    // Include id_token_hint if available (expired tokens are valid for logout)
    // post_logout_redirect_uri requires id_token_hint to be sent
    if (oidc_token?.IDToken) {
      params.set('id_token_hint', oidc_token.IDToken);
      params.set('post_logout_redirect_uri', redirectUri);
    }

    navigateToUrl(`${as.end_session_endpoint}?${params.toString()}`, {
      replace: true,
      reason: 'OIDC signOut end_session_endpoint redirect',
    });
  }

  public getTokens(): Promise<ClientTokens> {
    const tokens = localStorageHelper.get<ClientTokens>(AuthStorage.oidc_token_storage);
    return Promise.resolve(tokens || {});
  }

  public async handleCallback(props?: HandleCallbackProps): Promise<ClientTokens> {
    const clientId = props?.clientId || this.config.clientID;
    const redirectUri = props?.redirectUri || this.config.redirectUri;
    if (!clientId || !redirectUri) {
      throw new Error('oidc client not configured correctly, must provide both clientId and redirectUri');
    }

    const client: oauth.Client = {
      client_id: clientId,
      token_endpoint_auth_method: tokenEndpointAuthMethod,
    };
    const as = await this.getAuthenticationServer();
    // Consume the PKCE code_verifier up front (single-use, mirrors verifyAuthStateNonce).
    // The authorization code is single-use, so remove it from storage after we consume it.
    // Otherwise we could send a stale code_verifier against an already-consumed code, which
    // fails the token exchange with a 400 ("Could not process identity provider's response").
    const code_verifier = oidcSessionStore.getItem<string | null>(OIDC_SESSION_KEYS.codeVerifier, null);
    oidcSessionStore.removeItem(OIDC_SESSION_KEYS.codeVerifier);
    if (!code_verifier) {
      throw new Error('Code verifier not found');
    }
    // validate that we are on the expected URL
    const currentUrl: URL = new URL(window.location.href);
    // skip state check because we use PKCE
    const params = oauth.validateAuthResponse(as, client, currentUrl, oauth.skipStateCheck);
    if (oauth.isOAuth2Error(params)) {
      throw new Error('Could not validate callback parameters'); // Handle OAuth 2.0 redirect error
    }
    // NOTE the state parameter can contain application specific data. We verify it hasn't been tampered with via the `nonce`.
    // We don't use state in NWX currently but we could in the future.
    const state = params.get('state');
    const decodedAuthState = state ? decodeAuthState(state) : undefined;
    // Always verify (which consumes the single-use nonce from storage) so it can't linger
    // when state is missing or fails to decode. Only trust the state when the nonce matches.
    const nonceIsValid = this.verifyAuthStateNonce(decodedAuthState?.nonce);
    if (decodedAuthState && nonceIsValid) {
      this.authState = decodedAuthState;
    }

    const response = await oauth.authorizationCodeGrantRequest(
      as,
      client,
      params,
      redirectUri.toString(),
      code_verifier
    );

    const result = await oauth.processAuthorizationCodeOpenIDResponse(as, client, response);
    if (oauth.isOAuth2Error(result)) {
      throw new Error("Could not process identity provider's response"); // Handle OAuth 2.0 response body error
    }
    // Kept this commented so I can remember how to get the claims from the token
    // const claims = oauth.getValidatedIdTokenClaims(result);

    // store access token and id token in local storage
    const clientTokens: ClientTokens = {
      accessToken: (result as oauth.OpenIDTokenEndpointResponse).access_token,
      IDToken: (result as oauth.OpenIDTokenEndpointResponse).id_token,
    };

    localStorageHelper.create(AuthStorage.oidc_token_storage, clientTokens);

    return clientTokens;
  }

  public getSignInMethod(): Promise<SignInMethod> {
    return Promise.resolve('oidc');
  }

  public isUserAuthenticated(): boolean {
    const oidcTokens = localStorageHelper.get<ClientTokens>(AuthStorage.oidc_token_storage);
    const weaveTokenActive = isWeaveTokenActive();
    const oidcTokenActive = !!oidcTokens?.IDToken && !this.isOIDCTokenExpired(oidcTokens.IDToken);
    return weaveTokenActive || oidcTokenActive;
  }

  private isOIDCTokenExpired(IDToken: string): boolean {
    try {
      const decodedToken = jwtDecode<DecodedOIDCIDToken>(IDToken);
      const expirationDate = new Date(0);
      expirationDate.setUTCSeconds(decodedToken.exp);
      return expirationDate < new Date();
    } catch (e) {
      return true;
    }
  }
}
